import type { Event as SentryEvent, EventHint } from "@sentry/types";

/**
 * Reexportado com o nome antigo para não quebrar quem já importa
 * `SentryLikeEvent` (ex.: os testes deste módulo).
 */
export type SentryLikeEvent = SentryEvent;

export type BeforeSendFn = (
	event: SentryEvent,
	hint: EventHint
) => SentryEvent | null;

const NETWORK_ERROR_PATTERNS = [
	/Failed to fetch/i,
	/NetworkError/i,
	/HttpNetworkError/i,
	/HttpTimeoutError/i,
];

function isNetworkError(event: SentryEvent): boolean {
	const first = event.exception?.values?.[0];
	if (!first) return false;
	const haystack = `${first.type ?? ""} ${first.value ?? ""}`;
	return NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(haystack));
}

function defaultIsOnline(): boolean {
	return typeof navigator === "undefined" ? true : navigator.onLine;
}

/**
 * Encadeia um `beforeSend` existente com uma regra de rede: erros de rede
 * (`TypeError: Failed to fetch`, `HttpNetworkError`, `HttpTimeoutError`)
 * disparados enquanto o navegador está comprovadamente offline
 * (`navigator.onLine === false`) são descartados — não são acionáveis para o
 * backend. Os mesmos erros com o navegador online são mantidos e recebem a
 * tag `network_online` para facilitar a triagem no Sentry.
 */
export function buildNetworkAwareBeforeSend(
	baseBeforeSend: BeforeSendFn,
	isOnline: () => boolean = defaultIsOnline
): BeforeSendFn {
	return (event, hint) => {
		const processed = baseBeforeSend(event, hint);
		if (!processed) return processed;

		if (!isNetworkError(processed)) {
			return processed;
		}

		if (!isOnline()) {
			return null;
		}

		return {
			...processed,
			tags: { ...processed.tags, network_online: "true" },
		};
	};
}
